import type { Ref } from 'vue'
import { onMounted, nextTick } from '#imports'

type UseTableWrapperOptions = {
  /**
   * Class name used for the wrapping div.
   * Defaults to "msb-table-wrapper".
   */
  wrapperClass?: string
}

/**
 * Utility composable to detect <table> elements in a container
 * and wrap each of them with a div.msb-table-wrapper.
 *
 * Usage:
 *   const contentRef = ref<HTMLElement | null>(null)
 *   const { wrapTables } = useTableWrapper()
 *
 *   onMounted(() => {
 *     nextTick(() => {
 *       wrapTables(contentRef)
 *     })
 *   })
 */
export function useTableWrapper (options: UseTableWrapperOptions = {}) {
  const { wrapperClass = 'msb-table-wrapper' } = options

  const wrapTablesInElement = (root: HTMLElement | null) => {
    if (!root) return

    const tables = root.querySelectorAll<HTMLTableElement>('table')
    if (!tables.length) return

    tables.forEach((table) => {
      // Avoid wrapping twice
      if (table.closest(`.${wrapperClass}`)) {
        return
      }

      const wrapper = document.createElement('div')
      wrapper.classList.add(wrapperClass)

      const parent = table.parentNode
      if (!parent) return

      parent.insertBefore(wrapper, table)
      wrapper.appendChild(table)
    })
  }

  /**
   * Call this when you know the DOM is rendered.
   * Accepts either a Ref<HTMLElement | null> or a raw HTMLElement.
   */
  const wrapTables = (root: Ref<HTMLElement | null> | HTMLElement | null | undefined) => {
    if (!root) return

    if ((root as Ref<HTMLElement | null>).value !== undefined) {
      wrapTablesInElement((root as Ref<HTMLElement | null>).value)
    } else {
      wrapTablesInElement(root as HTMLElement | null)
    }
  }

  /**
   * Convenience helper: automatically wraps tables on mounted + nextTick
   * for a given ref.
   */
  const wrapOnMounted = (rootRef: Ref<HTMLElement | null>) => {
    onMounted(() => {
      nextTick(() => {
        wrapTables(rootRef)
      })
    })
  }

  return {
    wrapTables,
    wrapOnMounted
  }
}


